import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, STATUS_META, formatAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Search, MoreVertical, BarChart3, Trash2, Copy, ExternalLink, FileDown, Files, MessageSquare } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";

export default function Proposals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tous");

  const load = () => api.get("/proposals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const createNew = () => navigate("/proposals/coach");

  const remove = async (id) => {
    await api.delete(`/proposals/${id}`);
    toast.success("Proposition supprimée");
    load();
  };

  const copyLink = (publicId) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${publicId}`);
    toast.success("Lien public copié");
  };

  // Dupliquer une proposition (menu de liste) : utile pour toute proposition,
  // pas seulement les acceptées verrouillées (voir aussi ProposalEditor.js).
  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/proposals/${id}/duplicate`);
      toast.success("Copie créée");
      navigate(`/proposals/${data.id}/edit`);
    } catch (e) {
      toast.error(formatAxiosError(e));
    }
  };

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(`/proposals/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(p.title || "proposition").replace(/\s+/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Échec du téléchargement PDF");
    }
  };

  const statuses = ["tous", "brouillon", "envoyée", "vue", "acceptée", "refusée"];
  const filtered = items.filter((p) =>
    (filter === "tous" || p.status === filter) &&
    (p.title.toLowerCase().includes(q.toLowerCase()) || (p.client_name || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Gestion</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Propositions</h1>
        </div>
        <button onClick={createNew} data-testid="new-proposal-button"
          className="flex items-center gap-2 bg-primary text-white px-4 h-11 rounded-sm font-semibold hover:bg-primary/90 transition-all duration-200">
          <Plus className="w-4 h-4" /> Nouvelle proposition
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
            data-testid="proposal-search" className="pl-9 rounded-sm h-10" />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {statuses.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 h-10 rounded-sm text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                filter === s ? "bg-foreground text-white" : "bg-muted text-muted-foreground hover:text-foreground"
              }`}>
              {s === "tous" ? "Tous" : STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-border text-xs tracking-[0.1em] uppercase font-semibold text-muted-foreground">
          <div className="col-span-5">Proposition</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-2 text-right">Montant</div>
          <div className="col-span-2 text-center">Vues</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 && (
          <div className="px-6 py-16 text-center text-muted-foreground" data-testid="empty-proposals">
            Aucune proposition. Créez votre première proposition.
          </div>
        )}
        {filtered.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.brouillon;
          return (
            <div key={p.id} data-testid={`proposal-row-${p.id}`}
              className="grid grid-cols-12 items-center px-6 py-4 border-b border-border last:border-0 hover:bg-surface transition-colors duration-150 gap-2">
              <button onClick={() => navigate(`/proposals/${p.id}/edit`)} className="col-span-12 md:col-span-5 text-left">
                <p className="font-medium hover:text-primary transition-colors flex items-center gap-2">
                  {p.title}
                  {p.unread_messages > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary font-semibold"
                      title={`${p.unread_messages} message${p.unread_messages > 1 ? "s" : ""} non lu${p.unread_messages > 1 ? "s" : ""}`}
                      data-testid={`unread-messages-${p.id}`}
                    >
                      <MessageSquare className="w-3 h-3" /> {p.unread_messages}
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {p.client_name || "Sans client"}
                  {p.owner_name && user?.id && p.owner_id !== user.id && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground" title="Créée par un collaborateur">par {p.owner_name}</span>
                  )}
                </p>
              </button>
              <div className="col-span-6 md:col-span-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`} data-testid="proposal-status-badge">{meta.label}</span>
              </div>
              <div className="col-span-3 md:col-span-2 text-right font-mono text-sm font-medium">{fmtTotals(p.total_one_shot, p.total_recurring, p.currency)}</div>
              <div className="col-span-2 md:col-span-2 text-center text-sm text-muted-foreground">{p.views || 0}</div>
              <div className="col-span-1 flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid={`proposal-menu-${p.id}`} className="p-1.5 rounded-sm hover:bg-muted">
                    <MoreVertical className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-sm">
                    <DropdownMenuItem onClick={() => navigate(`/proposals/${p.id}/analytics`)} data-testid={`analytics-${p.id}`}>
                      <BarChart3 className="w-4 h-4 mr-2" /> Suivi & analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.open(`/p/${p.public_id}`, "_blank")}>
                      <ExternalLink className="w-4 h-4 mr-2" /> Aperçu public
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => copyLink(p.public_id)}>
                      <Copy className="w-4 h-4 mr-2" /> Copier le lien
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => downloadPdf(p)} data-testid={`pdf-${p.id}`}>
                      <FileDown className="w-4 h-4 mr-2" /> Télécharger le PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicate(p.id)} data-testid={`duplicate-${p.id}`}>
                      <Files className="w-4 h-4 mr-2" /> Dupliquer
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(p.id)} className="text-destructive" data-testid={`delete-${p.id}`}>
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
